import { useNavigate } from 'react-router-dom'
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Badge,
  Spinner,
  Alert,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi'
import { useAdminCommittees } from '../hooks/useAdminCommittees'
import { useAuth } from '../hooks/useAuth'
import { useMemo } from 'react'
import type { AdminCommitteeSummary } from '../types/admin'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'

type CommitteeRow = AdminCommitteeSummary & { depth: number }

/**
 * Flatten the committee list into a depth-first, indented display order. Recurses to
 * ARBITRARY depth (a grandchild lands at depth 2, not flattened to a top-level row -- WL-2),
 * carrying a numeric `depth` so the Name cell can indent proportionally rather than with a
 * single boolean. A committee whose parent isn't in the list (an orphan) is treated as a
 * top-level root at depth 0 so it still renders; a cycle can't loop forever thanks to the
 * visited guard, and any node left unvisited is appended flat so nothing is ever dropped.
 * Exported for unit testing the ordering/depth logic without rendering.
 */
export function flattenCommitteeTree(committees: AdminCommitteeSummary[]): CommitteeRow[] {
  const byId = new Set(committees.map(c => c.id))
  const childMap = new Map<string, AdminCommitteeSummary[]>()
  for (const c of committees) {
    if (c.parentCommitteeId) {
      const group = childMap.get(c.parentCommitteeId) ?? []
      group.push(c)
      childMap.set(c.parentCommitteeId, group)
    }
  }
  const result: CommitteeRow[] = []
  const visited = new Set<string>()
  const walk = (committee: AdminCommitteeSummary, depth: number) => {
    if (visited.has(committee.id)) return
    visited.add(committee.id)
    result.push({ ...committee, depth })
    const children = (childMap.get(committee.id) ?? []).sort((a, b) => a.label.localeCompare(b.label))
    for (const child of children) walk(child, depth + 1)
  }
  // Roots = no parent, or a parent that isn't present in the list (orphan -> render at top).
  const roots = committees.filter(c => !c.parentCommitteeId || !byId.has(c.parentCommitteeId))
  for (const root of roots) walk(root, 0)
  // Anything unreached (e.g. caught in a cycle) is appended flat so no committee is lost.
  for (const c of committees) {
    if (!visited.has(c.id)) result.push({ ...c, depth: 0 })
  }
  return result
}

export function AdminCommitteesPage() {
  const navigate = useNavigate()
  const { data: committees, isLoading, error } = useAdminCommittees()
  // WL-3: committee structure is provisioned by the vendor (SuperAdmin); a plain Admin can view
  // committees and edit their contact info, but not create them. The server enforces this — this
  // just hides a control that would 403.
  const { isSuperAdmin } = useAuth()

  // Sort hierarchically: parents first, descendants nested under them at increasing depth.
  const sortedCommittees = useMemo(
    () => (committees ? flattenCommitteeTree(committees) : []),
    [committees],
  )

  const columns: ResponsiveColumn<CommitteeRow>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (committee) => (
        <HStack gap={2}>
          {committee.depth > 0 && <Box w={`${committee.depth * 16}px`} />}
          <Text fontWeight={committee.depth > 0 ? 'normal' : 'semibold'}>
            {committee.depth > 0 ? '└ ' : ''}{committee.label}
          </Text>
        </HStack>
      ),
    },
    {
      header: 'City',
      mobileRole: 'meta',
      cell: (committee) => (
        <Text fontSize="sm" color="gray.600">{committee.city || '—'}</Text>
      ),
    },
    {
      header: 'County',
      mobileRole: 'meta',
      cell: (committee) => (
        <Text fontSize="sm" color="gray.600">{committee.county || '—'}</Text>
      ),
    },
    {
      header: 'Point of Contact',
      mobileRole: 'meta',
      cell: (committee) => (
        <Text fontSize="sm" color="gray.600">{committee.poc || '—'}</Text>
      ),
    },
    {
      header: 'Members',
      mobileRole: 'badge',
      isNumeric: true,
      cell: (committee) => (
        <Badge colorPalette={committee.memberCount > 0 ? 'blue' : 'gray'}>
          {committee.memberCount}
        </Badge>
      ),
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (committee) => (
        <Badge colorPalette={committee.isActive ? 'green' : 'gray'}>
          {committee.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      header: 'Created',
      mobileRole: 'desktop-only',
      cell: (committee) => (
        <Text fontSize="sm" color="gray.500">
          {new Date(committee.createdAt).toLocaleDateString()}
        </Text>
      ),
    },
  ]

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">Committees</Heading>
            <Text color="gray.500" mt={1}>
              {isSuperAdmin ? 'Create and manage committees' : 'View and edit committee details'}
            </Text>
          </Box>
          {isSuperAdmin && (
            <Button
              colorPalette="brand"
              onClick={() => navigate('/dashboard/admin/committees/new')}><FiPlus />Create Committee
                        </Button>
          )}
        </HStack>

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading committees...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load committees. Please try again.
          </Alert.Root>
        )}

        {!isLoading && committees && committees.length === 0 && (
          <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
            <Text color="gray.500" mb={4}>No committees yet.</Text>
            {isSuperAdmin && (
              <Button
                colorPalette="brand"
                onClick={() => navigate('/dashboard/admin/committees/new')}><FiPlus />Create Committee
                            </Button>
            )}
          </Box>
        )}

        {!isLoading && committees && committees.length > 0 && (
          <ResponsiveTable
            columns={columns}
            rows={sortedCommittees}
            keyFn={(committee) => committee.id}
            onRowClick={(committee) => navigate(`/dashboard/admin/committees/${committee.id}/edit`)}
          />
        )}
      </VStack>
    </Container>
  );
}
